import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Icon } from './Icon'
import styles from './Nav.module.css'

interface NavProps {
  userName: string
  isAdmin: boolean
  onLogout: () => void
}

export function Nav({ userName, isAdmin, onLogout }: NavProps) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const initials = userName
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <nav className={styles.nav}>
      <NavLink to="/" className={styles.logo}>
        <div className={styles.logoIcon}>
          <img
            src="/logo.png"
            alt="Review Ops"
            onError={e => {
              const el = e.currentTarget as HTMLImageElement
              el.style.display = 'none'
              el.parentElement!.style.background = 'var(--blue)'
              el.parentElement!.innerHTML = '<span class="mi" style="color:white;font-size:19px">rate_review</span>'
            }}
          />
        </div>
        Review Ops
      </NavLink>

      <div className={styles.spacer} />

      <div className={styles.tabs}>
        <NavLink
          to="/"
          end
          className={({ isActive }) => `${styles.tab} ${isActive ? styles.active : ''}`}
        >
          <Icon name="dashboard" size="sm" />
          Channels
        </NavLink>
        <NavLink
          to="/onboarding"
          className={({ isActive }) => `${styles.tab} ${isActive ? styles.active : ''}`}
        >
          <Icon name="add" size="sm" />
          Add Channel
        </NavLink>
      </div>

      <div className={styles.divider} />

      {/* Avatar + dropdown */}
      <div className={styles.avatarWrap} ref={menuRef}>
        <div
          className={styles.avatar}
          onClick={() => setOpen(o => !o)}
          title={userName}
        >
          {initials}
        </div>

        {open && (
          <div className={styles.menu}>
            <div className={styles.menuHeader}>
              <div className={styles.menuAvatar}>{initials}</div>
              <div>
                <div className={styles.menuName}>{userName}</div>
                <div className={styles.menuSub}>Signed in via Slack</div>
              </div>
            </div>
            <div className={styles.menuDivider} />
            {isAdmin && (
              <button className={styles.menuItem} onClick={() => { setOpen(false); navigate('/admin/channels') }}>
                <Icon name="admin_panel_settings" size="sm" />
                Manage channels
              </button>
            )}
            <button className={styles.menuItem} onClick={() => { setOpen(false); onLogout() }}>
              <Icon name="logout" size="sm" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </nav>
  )
}
